/**
 * genchemShared.js
 * Shared style tokens and primitive UI components used by
 * DiffHopInputs, DiffLinkerInputs, and GenChemUpload.
 *
 * Exported:
 *   s            — style object
 *   ToggleBtn    — two-option icon+label toggle button
 *   ModeSelector — compact auto/manual radio-style selector
 */

import React from 'react';

// ─── Shared style tokens ──────────────────────────────────────────────────────
export const s = {
  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #ede9f8',
    padding: '14px 14px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardLabel: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#1a1a2e',
    letterSpacing: '-0.01em',
  },
  cardSubLabel: {
    margin: 0,
    fontSize: 11,
    color: '#999',
    lineHeight: 1.5,
  },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    color: '#7c5cbf',
    background: '#ede9f8',
    borderRadius: 8,
    padding: '2px 8px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  toggleRow: {
    display: 'flex',
    gap: 8,
  },
  toggleBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 10px',
    borderRadius: 10,
    border: '1.5px solid',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    background: 'none',
    outline: 'none',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  // Mode selector (auto / manual row)
  modeSelectorRow: {
    display: 'flex',
    background: '#f5f3fb',
    borderRadius: 10,
    padding: 3,
    gap: 3,
  },
  modeSelectorBtn: {
    flex: 1,
    padding: '6px 0',
    borderRadius: 8,
    border: 'none',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: "'Inter', system-ui, sans-serif",
    fontWeight: 500,
    outline: 'none',
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  textInput: {
    width: '100%',
    padding: '9px 36px 9px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2ddf0',
    fontSize: 13,
    color: '#1a1a2e',
    background: '#fafafa',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  numberInput: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2ddf0',
    fontSize: 13,
    color: '#1a1a2e',
    background: '#fafafa',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  inputHint: {
    fontSize: 11,
    color: '#aaa',
    margin: 0,
  },
  errorMsg: {
    fontSize: 11,
    color: '#e05252',
    margin: 0,
  },
  uploadArea: {
    border: '1.5px dashed #d0c8ee',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    background: '#faf9fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadPrompt: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  uploadText: { fontSize: 12, color: '#888' },
  uploadExt: {
    fontFamily: "'JetBrains Mono', monospace",
    color: '#7c5cbf',
    fontWeight: 600,
    fontSize: 11,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  fileName: {
    fontSize: 12,
    color: '#444',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  // Info note (purple)
  infoNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    background: '#f5f0ff',
    border: '1px solid #d9cff5',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 11,
    color: '#5a3fa0',
    lineHeight: 1.5,
  },
  // Instruction box (amber — for manual-mode guidance)
  instructionBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    background: '#fffbeb',
    border: '1px solid #f5d87a',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 11,
    color: '#7a5a00',
    lineHeight: 1.6,
  },
  codeInline: {
    fontFamily: "'JetBrains Mono', monospace",
    background: '#f0ecfa',
    color: '#5a3fa0',
    padding: '0 4px',
    borderRadius: 4,
    fontSize: 11,
  },
};

// ─── ToggleBtn ────────────────────────────────────────────────────────────────
export function ToggleBtn({ active, onClick, icon, label }) {
  return (
    <button
      style={{
        ...s.toggleBtn,
        background:  active ? '#ede9f8' : '#fafafa',
        borderColor: active ? '#c4b5e8' : '#e2ddf0',
        color:       active ? '#5a3fa0' : '#666',
        fontWeight:  active ? 600 : 400,
      }}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── ModeSelector ─────────────────────────────────────────────────────────────
// Segmented control for 2-option mode choices (auto / manual)
export function ModeSelector({ value, onChange, options }) {
  return (
    <div style={s.modeSelectorRow}>
      {options.map((opt) => {
        const isActive = value === opt.value;
        return (
          <button
            key={opt.value}
            style={{
              ...s.modeSelectorBtn,
              background: isActive ? '#fff' : 'transparent',
              color:      isActive ? '#5a3fa0' : '#888',
              fontWeight: isActive ? 700 : 400,
              boxShadow:  isActive ? '0 1px 4px rgba(124,92,191,0.15)' : 'none',
            }}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}